import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import {
  Alert,
  Box,
  Button,
  Skeleton,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";

import { RADIUS, ICON, SURFACE, FONT } from "../../../theme/tokens";

import { SideDrawer } from "../../../components/SideDrawer";

import MainPanelContent, {
  CONTENT_MAX_WIDTH,
} from "../components/MainPanelContent";

import { useDatasets } from "../../../hooks/useDatasets";

import DatasetCard from "./DatasetCard";
import DatasetDetails from "./DatasetDetails";
import { datasetBrowsePath, filterAndSortDatasets } from "./datasetDisplay";

const GRID_SX = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 320px))",
  gap: { xs: 2, sm: 2.5 },
  alignItems: "stretch",
  width: "100%",
};

export default function Datasets() {
  const { datasets, loading, error, refreshDatasets } = useDatasets();

  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("updated");
  const visibleDatasets = useMemo(
    () => filterAndSortDatasets(datasets, query, sortBy),
    [datasets, query, sortBy],
  );
  const [selectedId, setSelectedId] = useState(null);
  const selectedDataset = datasets.find((d) => d.id === selectedId);
  const navigate = useNavigate();
  const browse = (dataset) => {
    const path = datasetBrowsePath(dataset);
    if (!path) return;
    setSelectedId(null);
    navigate("/workspace?tab=overview/storage", {
      state: {
        storageBrowserTarget: { source: "datasets", initialPath: path },
      },
    });
  };
  const openDetails = (id) => {
    setSelectedId(id);
    refreshDatasets();
  };

  return (
    <MainPanelContent
      title="Datasets"
      subtitle={
        "Research datasets for your workspaces and jobs. View details or browse files."
      }
      maxWidth={CONTENT_MAX_WIDTH}
      contentGap={3}
      headerMb={1.5}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.25,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search datasets"
          inputProps={{ "aria-label": "Search datasets by name" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: ICON, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: "1 1 320px",
            "& .MuiOutlinedInput-root": {
              borderRadius: `${RADIUS.sm}px`,
              bgcolor: SURFACE,
            },
          }}
        />
        <Select
          size="small"
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value)}
          inputProps={{ "aria-label": "Sort datasets" }}
          startAdornment={
            <InputAdornment position="start">
              <SortRoundedIcon sx={{ color: ICON, fontSize: 18 }} />
            </InputAdornment>
          }
          sx={{
            width: { xs: "100%", sm: 220 },
            borderRadius: `${RADIUS.sm}px`,
            bgcolor: SURFACE,
            fontSize: FONT.sm,
          }}
        >
          <MenuItem value="updated">Recently updated</MenuItem>
          <MenuItem value="name">Name</MenuItem>
          <MenuItem value="size">Largest</MenuItem>
          <MenuItem value="generation">Highest generation</MenuItem>
        </Select>
        <Button
          onClick={refreshDatasets}
          disabled={loading}
          sx={{ ml: "auto", flexShrink: 0 }}
        >
          Refresh
        </Button>
      </Box>
      {/* Inline error banner — shown without unmounting the rest of the page */}
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refreshDatasets}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading && !datasets.length ? (
        <Box sx={GRID_SX}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={240}
              sx={{ borderRadius: `${RADIUS.md}px` }}
            />
          ))}
        </Box>
      ) : visibleDatasets.length ? (
        <Box sx={GRID_SX}>
          {visibleDatasets.map((d) => (
            <DatasetCard
              key={d.id}
              dataset={d}
              onOpenDetails={openDetails}
              onBrowse={browse}
            />
          ))}
        </Box>
      ) : !error ? (
        <Alert
          severity="info"
          action={
            query.trim() && datasets.length ? (
              <Button color="inherit" onClick={() => setQuery("")}>
                Clear search
              </Button>
            ) : undefined
          }
        >
          {datasets.length
            ? "No datasets match your search."
            : "No datasets found."}
        </Alert>
      ) : null}
      <SideDrawer
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={selectedDataset?.title || "Dataset details"}
        optionalHeader={
          selectedDataset && (
            <Button
              disabled={
                loading || Boolean(error) || !datasetBrowsePath(selectedDataset)
              }
              onClick={() => browse(selectedDataset)}
              startIcon={<FolderOpenOutlinedIcon />}
              sx={{ textTransform: "none" }}
            >
              Browse files
            </Button>
          )
        }
      >
        {loading ? (
          <StackPlaceholder />
        ) : error ? (
          <Alert
            severity="error"
            action={<Button onClick={refreshDatasets}>Retry</Button>}
          >
            {error}
          </Alert>
        ) : selectedDataset ? (
          <DatasetDetails dataset={selectedDataset} />
        ) : (
          <Alert severity="info">This dataset is no longer available.</Alert>
        )}
      </SideDrawer>
    </MainPanelContent>
  );
}

function StackPlaceholder() {
  return (
    <Box aria-label="Loading dataset details">
      <Skeleton height={32} />
      <Skeleton height={180} />
      <Skeleton height={180} />
    </Box>
  );
}
